import { getDateFromServer, getDateFromServerUTC, isPrerenderMode } from '@app/config/util';
import { log, LOG_LEVELS } from '@common/config/app_logger';
import { calculateDifference, getDataFromStorage, paramOrNul } from '@common/config/utils';
import { getDiscountPercent } from '@app/project_items/coupon_items/coupon_utils';
import { type OverridedMixpanel } from 'mixpanel-browser';
import Cookies from 'universal-cookie';
import { IAnalyticsProvider } from './IAnalyticsProvider';
import { type EventQueueItem } from '.';
import { Subscription } from './EventEmitter';
import { AnalyticsProvider } from './AnalyticsProvider';
import { MixpanelProvider } from './MixpanelProvider';

const cookies = new Cookies();

const handleTimedAnalytics = (user: any, dynamicConfig: any) => {
  const timedAnalyticsProps: Record<string, any> = {};
  if (user?.analyticsObject?.timedAnalytics?.length > 0) {
    const { analyticsObject: { timedAnalytics } } = user;

    timedAnalytics.forEach((timedAnalytic: any) => {
      const { analytics, expiresAt } = timedAnalytic;
      if (new Date(expiresAt) > getDateFromServer(dynamicConfig?.utc)) {
        Object.keys(analytics).forEach((key) => {
          if (Array.isArray(analytics[key]) && !!timedAnalyticsProps[key]) {
            timedAnalyticsProps[key] = Array.isArray(timedAnalyticsProps[key])
              ? [...timedAnalyticsProps[key], ...analytics[key]] : [timedAnalyticsProps[key], ...analytics[key]];
            return;
          }
          timedAnalyticsProps[key] = analytics[key];
        });
      }
    });
  }
  return timedAnalyticsProps;
};

const getActiveAdvisorDiscount = (eventProperties: any, advisors: any) => {
  if (!eventProperties || !advisors) return {};
  const advisorId = eventProperties['advisor id'];
  const advisor = advisors[advisorId];
  const discountCouponId = advisor?.discountOffer?.discountCouponId;
  if (!advisor || !discountCouponId) return {};
  return { 'active advisor discount': `${ getDiscountPercent({ discountCouponId }) }%` };
};

const getAdvisorAnalytics = (eventProperties: any, advisors: any) => {
  if (!eventProperties || !advisors) return {};
  const advisorId = eventProperties['advisor id'];
  const advisor = advisors[advisorId];
  if (!advisor || !advisor.analytics) return {};
  return advisor.analytics;
};

export class AnalyticsStrategy implements IAnalyticsProvider {
  private providers: AnalyticsProvider[];

  private queue: { [key: string]: EventQueueItem[] } = {};

  private subscription: Subscription;

  public get name(): string { throw new Error('Not implemented'); }

  constructor(providers: AnalyticsProvider[]) {
    this.providers = providers.filter(x => !!x);
  }

  private saveToQueue(provider: IAnalyticsProvider, eventName: string, eventProperties?: Record<string, any>, additionalProperties?: Record<string, any>) {
    if (!this.queue[provider.name]) this.queue[provider.name] = [];
    if (this.queue[provider.name].length > 20) return; // preventing memory leak
    this.queue[provider.name].push({ eventName, eventProperties, additionalProperties });
  }

  private processQueue(provider: IAnalyticsProvider) {
    let item: EventQueueItem = this.queue[provider.name]?.shift();
    while (item) {
      provider.trackEvent(item.eventName, item.eventProperties, item.additionalProperties);
      item = this.queue[provider.name]?.shift();
    }
  }

  public get initialized() { return true; }

  public init() {
    this.providers.forEach(p => {
      p.init(() => this.processQueue(p));
    });
  }

  public authorize(userId: string, params?: Record<string, any>) {
    this.providers.forEach(p => p.authorize(userId, params));
  }

  public reset() {
    this.providers.forEach(p => p.reset());
  }

  public trackEvent(eventName: string, eventProperties?: Record<string, any>, additionalProperties?: Record<string, any>) {
    if (isPrerenderMode()) return;

    const { application: { dynamicConfig, anonymousUser }, user: { user }, advisors } = window.store.getState();
    const deviceId = getDataFromStorage('DEVICE_ID');

    const userAnalytics = user?.analytics || anonymousUser?.analytics;
    const date = getDateFromServerUTC(dynamicConfig?.utc);

    const mergedEventProperties: Record<string, any> = {
      ...handleTimedAnalytics(user, dynamicConfig),
      ...dynamicConfig?.analytics,
      ...userAnalytics,
      ...paramOrNul('days since signup', calculateDifference({ date1: date, date2: userAnalytics?.['signup date'], type: 'days' })),
      ...paramOrNul('days since first purchase', calculateDifference({ date1: date, date2: userAnalytics?.['first purchase date'], type: 'days' })),
      ...paramOrNul('days since last live session', calculateDifference({ date1: date, date2: userAnalytics?.['last live session date'], type: 'days' })),
      ...paramOrNul('days since fbm', calculateDifference({ date1: date, date2: userAnalytics?.['fbm date'], type: 'days' })),
      ...paramOrNul('days since first visit', calculateDifference({ date1: date, date2: userAnalytics?.['first visit date'], type: 'days' })),
      ...getActiveAdvisorDiscount(eventProperties, advisors),
      ...getAdvisorAnalytics(eventProperties, advisors),
      'brand device id': deviceId,
      ...paramOrNul('kueez user id', cookies.get('facebook_user_id')),
      ...eventProperties
    };

    this.providers.forEach(provider => {
      if (!provider.initialized) {
        log(LOG_LEVELS.INFO, 'Analytics', `saving event to queue ${ eventName }, provider: ${ provider.name }`);
        this.saveToQueue(provider, eventName, mergedEventProperties, additionalProperties);
        return;
      }

      const properties = provider.mapAndExtendProperties(eventName, mergedEventProperties);
      provider.trackEvent(eventName, properties, additionalProperties);
    });
  }

  public trackPageVisit(pageTitle: string): void {
    if (isPrerenderMode()) return;
    this.providers.forEach(p => p.trackPageVisit(pageTitle));
  }

  public getMixpanelInstance(callback: (instance: OverridedMixpanel) => void) {
    const mixpanelProvider = this.providers.find(x => x instanceof MixpanelProvider) as MixpanelProvider;
    if (mixpanelProvider) mixpanelProvider.getInstance(callback);
  }
}
